import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Alert,
  Avatar,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  FormControlLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Step,
  StepLabel,
  Stepper,
  TextField,
  Typography,
} from "@mui/material";
import {
  ContentCopyOutlined as CopyIcon,
  InfoOutlined as InfoIcon,
} from "@mui/icons-material";
import { ROUTES } from "@/routes/config";
import PageLayout from "@/components/PageLayout";
import { ColorField, LogoUpload } from "@/components/form";
import { useMasterLlmModels } from "@/containers/PortalDetails/hooks/useMasterLlmModels";
import { usePortals } from "../hooks/usePortals";
import { usePortalDetails } from "@/containers/PortalDetails/hooks/usePortalDetails";
import apiClient from "@/services/api/client";
import { API_ENDPOINTS } from "@/services/api/endpoints";
import type { CreatePortalPayload, PortalLlmModel } from "@/types";

const STEPS = [
  "Source portal",
  "Basics",
  "Branding",
  "Policy & AI",
  "Review & create",
];

interface WizardState {
  sourcePortalId: string;
  name: string;
  contact_name: string;
  contact_email: string;
  contact_phone: string;
  primary_color: string;
  secondary_color: string;
  logo_url: string;
  embedding_provider: string;
  embedding_model: string;
}

const CopyPortalWizard: React.FC = () => {
  const navigate = useNavigate();
  const { portals, isLoading: portalsLoading } = usePortals();
  const { createPortalAsync, isCreating } = usePortals();
  const [activeStep, setActiveStep] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [selectedModelIds, setSelectedModelIds] = useState<string[]>([]);

  const [state, setState] = useState<WizardState>({
    sourcePortalId: "",
    name: "",
    contact_name: "",
    contact_email: "",
    contact_phone: "",
    primary_color: "",
    secondary_color: "",
    logo_url: "",
    embedding_provider: "",
    embedding_model: "",
  });

  const { portal: source, isLoading: sourceLoading } = usePortalDetails(
    state.sourcePortalId || undefined,
  );
  const { embeddingModels, isLoading: isLoadingModels } = useMasterLlmModels();

  const embeddingProviderOptions = useMemo(() => {
    const providers = [...new Set(embeddingModels.map((m) => m.provider))].sort();
    return providers.map((p) => ({ label: p, value: p }));
  }, [embeddingModels]);

  const embeddingModelOptions = useMemo(() => {
    if (!state.embedding_provider) return [];
    return embeddingModels
      .filter((m) => m.provider === state.embedding_provider)
      .map((m) => ({ label: m.model_name, value: m.model_id }));
  }, [embeddingModels, state.embedding_provider]);

  const activePortals = useMemo(
    () => portals.filter((p) => p.status === "active"),
    [portals],
  );

  const slug = useMemo(
    () =>
      (state.name || "")
        .trim()
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, "-")
        .replace(/^-+|-+$/g, "")
        .slice(0, 80),
    [state.name],
  );

  // When the user picks a source portal OR when the source loads, pre-fill
  // fields they haven't manually edited yet. We only overwrite blank fields
  // so repeated source changes don't stomp user edits.
  const [prefillKey, setPrefillKey] = useState<string>("");
  if (source && source.id !== prefillKey) {
    setPrefillKey(source.id);
    setState((s) => ({
      ...s,
      contact_name: s.contact_name || source.contact_name || "",
      contact_email: s.contact_email || source.contact_email || "",
      contact_phone: s.contact_phone || source.contact_phone || "",
      primary_color: s.primary_color || source.branding?.primary_color || "",
      secondary_color:
        s.secondary_color || source.branding?.secondary_color || "",
      logo_url: s.logo_url || source.branding?.logo_url || "",
      embedding_provider:
        s.embedding_provider || source.settings?.embedding_provider || "",
      embedding_model:
        s.embedding_model || source.settings?.embedding_model || "",
    }));
    // Pre-select all source LLM models
    if (source.llm_models?.length) {
      setSelectedModelIds(source.llm_models.map((m) => m.model_id));
    }
  }

  const set = <K extends keyof WizardState>(key: K, value: WizardState[K]) =>
    setState((s) => ({ ...s, [key]: value }));

  const toggleModel = (modelId: string) => {
    setSelectedModelIds((prev) =>
      prev.includes(modelId)
        ? prev.filter((id) => id !== modelId)
        : [...prev, modelId],
    );
  };

  const canContinue = (): boolean => {
    if (activeStep === 0) return !!state.sourcePortalId;
    if (activeStep === 1) return state.name.trim().length > 0;
    return true;
  };

  const handleNext = () => {
    if (!canContinue()) return;
    setError(null);
    setActiveStep((s) => Math.min(s + 1, STEPS.length - 1));
  };

  const handleBack = () => {
    setError(null);
    setActiveStep((s) => Math.max(s - 1, 0));
  };

  const handleCreate = async () => {
    setError(null);
    try {
      const payload: CreatePortalPayload = {
        slug,
        name: state.name.trim(),
        ...(state.contact_name.trim() && {
          contact_name: state.contact_name.trim(),
        }),
        ...(state.contact_email.trim() && {
          contact_email: state.contact_email.trim(),
        }),
        ...(state.contact_phone.trim() && {
          contact_phone: state.contact_phone.trim(),
        }),
        ...((state.embedding_provider || state.embedding_model) && {
          settings: {
            embedding_provider: state.embedding_provider || null,
            embedding_model: state.embedding_model || null,
          },
        }),
        ...((state.primary_color ||
          state.secondary_color ||
          state.logo_url) && {
          branding: {
            ...(state.primary_color && { primary_color: state.primary_color }),
            ...(state.secondary_color && {
              secondary_color: state.secondary_color,
            }),
            ...(state.logo_url && { logo_url: state.logo_url }),
          },
        }),
      };
      const created = await createPortalAsync(payload);

      // Copy selected LLM models to the new portal via PATCH
      if (selectedModelIds.length > 0 && created?.id) {
        try {
          const llmModels = selectedModelIds.map((model_id, i) => ({
            model_id,
            is_default: i === 0,
            is_enabled: true,
          }));
          await apiClient.patch(
            API_ENDPOINTS.SYSTEM.PORTALS.DETAIL(created.id),
            { llm_models: llmModels },
          );
        } catch {
          // Non-fatal — portal was created, models can be added later
          console.warn("Failed to copy LLM models to new portal");
        }
      }
      // createPortalAsync navigates to the new portal details page on success.
    } catch (err: unknown) {
      const e = err as {
        response?: { data?: { detail?: string; message?: string } };
        message?: string;
      };
      setError(
        e?.response?.data?.detail ||
          e?.response?.data?.message ||
          e?.message ||
          "Failed to create portal",
      );
    }
  };

  const isLast = activeStep === STEPS.length - 1;

  return (
    <PageLayout
      title="Create from existing portal"
      subtitle="Pick a source portal, confirm the configuration, then create the new portal."
      backTo={ROUTES.CREATE_PORTAL}
    >
      <Stack spacing={3}>
        {error && (
          <Alert severity="error" onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        <Paper
          elevation={0}
          sx={{ p: 3, border: "1px solid", borderColor: "divider" }}
        >
          <Stepper activeStep={activeStep} alternativeLabel>
            {STEPS.map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>
        </Paper>

        <Paper
          elevation={0}
          sx={{ p: 3, border: "1px solid", borderColor: "divider" }}
        >
          {activeStep === 0 && (
            <StepSource
              portals={activePortals}
              loading={portalsLoading}
              value={state.sourcePortalId}
              onChange={(v) => {
                set("sourcePortalId", v);
                setPrefillKey(""); // force re-prefill when source changes
              }}
            />
          )}

          {activeStep === 1 && (
            <StepBasics
              name={state.name}
              slug={slug}
              contact_name={state.contact_name}
              contact_email={state.contact_email}
              contact_phone={state.contact_phone}
              onChange={set}
              sourceName={source?.name}
              sourceLoading={sourceLoading}
            />
          )}

          {activeStep === 2 && (
            <StepBranding
              primary_color={state.primary_color}
              secondary_color={state.secondary_color}
              logo_url={state.logo_url}
              onChange={set}
              sourceLoading={sourceLoading}
            />
          )}

          {activeStep === 3 && (
            <StepPolicy
              embedding_provider={state.embedding_provider}
              embedding_model={state.embedding_model}
              providerOptions={embeddingProviderOptions}
              modelOptions={embeddingModelOptions}
              isLoadingModels={isLoadingModels}
              onChange={set}
              sourceLoading={sourceLoading}
              sourceLlmModels={source?.llm_models ?? []}
              selectedModelIds={selectedModelIds}
              onToggleModel={toggleModel}
            />
          )}

          {activeStep === 4 && (
            <StepReview
              state={state}
              slug={slug}
              sourceName={source?.name ?? "—"}
              selectedModelIds={selectedModelIds}
            />
          )}
        </Paper>

        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Button
            onClick={handleBack}
            disabled={activeStep === 0 || isCreating}
          >
            Back
          </Button>
          <Stack direction="row" spacing={1}>
            <Button onClick={() => navigate(ROUTES.CREATE_PORTAL)} disabled={isCreating}>
              Cancel
            </Button>
            {!isLast && (
              <Button
                variant="contained"
                onClick={handleNext}
                disabled={!canContinue()}
              >
                Continue
              </Button>
            )}
            {isLast && (
              <Button
                variant="contained"
                startIcon={isCreating ? <CircularProgress size={16} /> : <CopyIcon />}
                onClick={handleCreate}
                disabled={isCreating}
              >
                {isCreating ? "Creating..." : "Create Portal"}
              </Button>
            )}
          </Stack>
        </Stack>
      </Stack>
    </PageLayout>
  );
};

// --- Step components ---------------------------------------------------------

interface StepSourceProps {
  portals: Array<{ id: string; name: string; slug: string; status: string }>;
  loading: boolean;
  value: string;
  onChange: (id: string) => void;
}

const StepSource: React.FC<StepSourceProps> = ({
  portals,
  loading,
  value,
  onChange,
}) => (
  <Stack spacing={2}>
    <Typography variant="h6" fontWeight={700}>
      Choose source portal
    </Typography>
    <Alert icon={<InfoIcon fontSize="small" />} severity="info">
      Only <strong>active</strong> portals can be used as a source. Pending,
      provisioning, suspended, and failed portals are hidden.
    </Alert>
    {loading ? (
      <Stack direction="row" spacing={1} alignItems="center">
        <CircularProgress size={18} />
        <Typography variant="body2">Loading portals...</Typography>
      </Stack>
    ) : portals.length === 0 ? (
      <Alert severity="warning">
        No active portals available. Create and activate a portal first, then
        return here.
      </Alert>
    ) : (
      <Select
        value={value}
        onChange={(e) => onChange(e.target.value as string)}
        displayEmpty
        sx={{ maxWidth: 480, width: "100%" }}
      >
        <MenuItem value="">
          <em>Select a source portal</em>
        </MenuItem>
        {portals.map((p) => (
          <MenuItem key={p.id} value={p.id}>
            <Stack direction="row" spacing={1.5} alignItems="center">
              <Typography fontWeight={600}>{p.name}</Typography>
              <Typography variant="caption" color="text.secondary">
                {p.slug}
              </Typography>
              <Chip label="Active" color="success" size="small" />
            </Stack>
          </MenuItem>
        ))}
      </Select>
    )}
  </Stack>
);

interface StepBasicsProps {
  name: string;
  slug: string;
  contact_name: string;
  contact_email: string;
  contact_phone: string;
  onChange: <K extends keyof WizardState>(key: K, value: WizardState[K]) => void;
  sourceName?: string;
  sourceLoading: boolean;
}

const StepBasics: React.FC<StepBasicsProps> = ({
  name,
  slug,
  contact_name,
  contact_email,
  contact_phone,
  onChange,
  sourceName,
  sourceLoading,
}) => (
  <Stack spacing={1.5}>
    <Typography variant="h6" fontWeight={700}>
      New portal basics
    </Typography>
    <Typography variant="body2" color="text.secondary">
      Copying from: {sourceLoading ? "..." : <strong>{sourceName ?? "—"}</strong>}
      . Enter a unique name for the new portal. Contact details were pre-filled
      from the source — edit as needed.
    </Typography>
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
        columnGap: 2,
        rowGap: 1.5,
      }}
    >
      <TextField
        label="Portal name"
        value={name}
        onChange={(e) => onChange("name", e.target.value)}
        required
        size="small"
        helperText={slug ? `Slug: ${slug}` : "Enter a name to generate a slug"}
      />
      <TextField
        label="Contact name"
        value={contact_name}
        onChange={(e) => onChange("contact_name", e.target.value)}
        size="small"
      />
      <TextField
        label="Contact email"
        value={contact_email}
        onChange={(e) => onChange("contact_email", e.target.value)}
        size="small"
      />
      <TextField
        label="Contact phone"
        value={contact_phone}
        onChange={(e) => onChange("contact_phone", e.target.value)}
        size="small"
      />
    </Box>
  </Stack>
);

interface StepBrandingProps {
  primary_color: string;
  secondary_color: string;
  logo_url: string;
  onChange: <K extends keyof WizardState>(key: K, value: WizardState[K]) => void;
  sourceLoading: boolean;
}

const StepBranding: React.FC<StepBrandingProps> = ({
  primary_color,
  secondary_color,
  logo_url,
  onChange,
  sourceLoading,
}) => (
  <Stack spacing={2}>
    <Typography variant="h6" fontWeight={700}>
      Branding
    </Typography>
    <Typography variant="body2" color="text.secondary">
      {sourceLoading
        ? "Loading source branding..."
        : "Pre-filled from the source portal. Edit to customise."}
    </Typography>
    <LogoUpload
      label="Logo"
      value={logo_url}
      onChange={(v) => onChange("logo_url", v)}
      disabled={sourceLoading}
    />
    <ColorField
      label="Primary color"
      value={primary_color}
      onChange={(v) => onChange("primary_color", v)}
      fullWidth
    />
    <ColorField
      label="Secondary color"
      value={secondary_color}
      onChange={(v) => onChange("secondary_color", v)}
      fullWidth
    />
  </Stack>
);

interface StepPolicyProps {
  embedding_provider: string;
  embedding_model: string;
  providerOptions: Array<{ label: string; value: string }>;
  modelOptions: Array<{ label: string; value: string }>;
  isLoadingModels: boolean;
  onChange: <K extends keyof WizardState>(key: K, value: WizardState[K]) => void;
  sourceLoading: boolean;
  sourceLlmModels: PortalLlmModel[];
  selectedModelIds: string[];
  onToggleModel: (modelId: string) => void;
}

const StepPolicy: React.FC<StepPolicyProps> = ({
  embedding_provider,
  embedding_model,
  providerOptions,
  modelOptions,
  isLoadingModels,
  onChange,
  sourceLoading,
  sourceLlmModels,
  selectedModelIds,
  onToggleModel,
}) => (
  <Stack spacing={2}>
    <Typography variant="h6" fontWeight={700}>
      Policy & AI configuration
    </Typography>
    <Alert severity="warning">
      <strong>Infrastructure is reprovisioned for each portal.</strong>
      <Typography variant="body2" sx={{ mt: 0.5 }}>
        Database, S3 bucket, vector index, memory store, and LLM team are
        always created fresh. You will need to reconfigure these manually in
        the new portal after it becomes active: <strong>custom domain</strong>,{" "}
        <strong>SSO provider</strong>, <strong>SMTP credentials</strong>, and{" "}
        <strong>any external API keys</strong>.
      </Typography>
    </Alert>
    <Typography variant="body2" color="text.secondary">
      {sourceLoading
        ? "Loading source policy..."
        : "Embedding config is pre-filled from source. Select which LLM chat models to copy."}
    </Typography>
    <TextField
      select
      label="Embedding provider"
      value={embedding_provider}
      onChange={(e) => {
        onChange("embedding_provider", e.target.value);
        onChange("embedding_model", "");
      }}
      fullWidth
      disabled={isLoadingModels}
    >
      {providerOptions.map((opt) => (
        <MenuItem key={opt.value} value={opt.value}>
          {opt.label}
        </MenuItem>
      ))}
    </TextField>
    <TextField
      select
      label="Embedding model"
      value={embedding_model}
      onChange={(e) => onChange("embedding_model", e.target.value)}
      fullWidth
      disabled={isLoadingModels || !embedding_provider}
    >
      {modelOptions.map((opt) => (
        <MenuItem key={opt.value} value={opt.value}>
          {opt.label}
        </MenuItem>
      ))}
    </TextField>

    {sourceLlmModels.length > 0 && (
      <Box>
        <Typography variant="subtitle2" sx={{ mb: 1 }}>
          LLM chat models from source portal
        </Typography>
        <Paper
          elevation={0}
          sx={{ border: "1px solid", borderColor: "divider", p: 1.5 }}
        >
          <Stack spacing={0.5}>
            {sourceLlmModels.map((m) => (
              <FormControlLabel
                key={m.model_id}
                control={
                  <Checkbox
                    checked={selectedModelIds.includes(m.model_id)}
                    onChange={() => onToggleModel(m.model_id)}
                    size="small"
                  />
                }
                label={
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Typography variant="body2">{m.model_id}</Typography>
                    {m.is_default && (
                      <Chip label="Default" size="small" color="primary" variant="outlined" />
                    )}
                    {!m.is_enabled && (
                      <Chip label="Disabled" size="small" color="default" variant="outlined" />
                    )}
                  </Stack>
                }
              />
            ))}
          </Stack>
        </Paper>
        <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: "block" }}>
          Selected models will be assigned to the new portal. The first selected
          model will be set as default.
        </Typography>
      </Box>
    )}
    {sourceLlmModels.length === 0 && !sourceLoading && (
      <Typography variant="body2" color="text.secondary">
        No LLM chat models assigned to the source portal.
      </Typography>
    )}
  </Stack>
);

interface StepReviewProps {
  state: WizardState;
  slug: string;
  sourceName: string;
  selectedModelIds: string[];
}

const StepReview: React.FC<StepReviewProps> = ({ state, slug, sourceName, selectedModelIds }) => (
  <Stack spacing={2}>
    <Typography variant="h6" fontWeight={700}>
      Review & create
    </Typography>
    <Alert severity="info">
      Clicking <strong>Create Portal</strong> will start provisioning the new
      portal (database, S3, vector index, etc.). This typically takes 2–5
      minutes. After the portal is <strong>active</strong>, open its details
      page and use the <strong>Import Content</strong> section to copy agents,
      CMS pages, and files from the source portal.
    </Alert>

    <ReviewSection title="Source" rows={[["Source portal", sourceName]]} />

    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
        gap: 2,
      }}
    >
      <ReviewSection
        title="New portal"
        rows={[
          ["Name", state.name || "—"],
          ["Slug", slug || "—"],
          ["Contact name", state.contact_name || "—"],
          ["Contact email", state.contact_email || "—"],
          ["Contact phone", state.contact_phone || "—"],
        ]}
      />
      <Stack spacing={2}>
        <ReviewSection
          title="Branding"
          rows={[
            ["Logo", state.logo_url ? (
              <Avatar
                src={state.logo_url}
                variant="rounded"
                sx={{ width: 48, height: 48, bgcolor: "background.default" }}
                imgProps={{ style: { objectFit: "contain" } }}
              />
            ) : "—"],
            ["Primary color", state.primary_color || "—"],
            ["Secondary color", state.secondary_color || "—"],
          ]}
        />
        <ReviewSection
          title="AI"
          rows={[
            ["Embedding provider", state.embedding_provider || "—"],
            ["Embedding model", state.embedding_model || "—"],
            ["LLM models", selectedModelIds.length > 0
              ? (
                <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                  {selectedModelIds.map((id) => (
                    <Chip key={id} label={id} size="small" variant="outlined" />
                  ))}
                </Stack>
              )
              : "None"],
          ]}
        />
      </Stack>
    </Box>

    <Alert severity="warning">
      <strong>Not copied at creation:</strong> Knowledge bases, roles &
      permissions, SSO providers, SMTP credentials, custom domains, external
      API keys. Set these up manually in the new portal.
    </Alert>
  </Stack>
);

const ReviewSection: React.FC<{
  title: string;
  rows: Array<[string, React.ReactNode]>;
}> = ({ title, rows }) => (
  <Box>
    <Typography variant="subtitle2" sx={{ mb: 1 }}>
      {title}
    </Typography>
    <Paper
      elevation={0}
      sx={{
        border: "1px solid",
        borderColor: "divider",
        overflow: "hidden",
      }}
    >
      {rows.map(([k, v], i) => (
        <Stack
          key={k}
          direction="row"
          sx={{
            px: 1.5,
            py: 0.75,
            borderTop: i === 0 ? "none" : "1px solid",
            borderColor: "divider",
          }}
        >
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ width: 140, flexShrink: 0 }}
          >
            {k}
          </Typography>
          <Box sx={{ wordBreak: "break-word", minWidth: 0 }}>
            {typeof v === "string" ? (
              <Typography variant="body2">{v}</Typography>
            ) : (
              v
            )}
          </Box>
        </Stack>
      ))}
    </Paper>
  </Box>
);

export default CopyPortalWizard;
